import Phaser from 'phaser';
import { Socket } from 'socket.io-client';

 import { getLoadingTip } from '../../utils/loadingTips';

// Grid-based world map - all visuals are squares
const TILE_SIZE = 16;  // Base tile size

const UNCHARTED_WATERS_BUFFER_TILES = 64;
const UNCHARTED_WATERS_BUFFER_PX = UNCHARTED_WATERS_BUFFER_TILES * TILE_SIZE;
const UNCHARTED_WATERS_COLOR = 0x050b14;

interface Point {
  x: number;
  y: number;
}

interface Island {
  id: string;
  x: number;
  y: number;
  radius: number;
  type: string;
  biome: string;
  shape?: Point[];  // Polygon shape data
  isPermanent: boolean;
  permanentName: string | null;
  ownerId: string | null;
  ownerType: string | null;
  ownerName: string | null;
  customName: string | null;
  claimRadius: number | null;
}

interface IslandSprite extends Phaser.GameObjects.Rectangle {
  islandData: Island;
  nameText?: Phaser.GameObjects.Text;
  territoryRect?: Phaser.GameObjects.Rectangle;
  graphics?: Phaser.GameObjects.Graphics;
  icon?: Phaser.GameObjects.Text;
  labels?: Phaser.GameObjects.Text[];
  chunkId?: string;
  raidOverlay?: Phaser.GameObjects.Graphics;
  raidTween?: Phaser.Tweens.Tween;
}

interface PirateRaidData {
  id: string;
  islandId: string;
  islandName: string;
  islandX: number;
  islandY: number;
  tier: string;
  expiresAt: number;
  piratesDefeated: number;
  totalPirates: number;
  rewardPool: number;
}

interface WhirlpoolInstanceMapData {
  id: string;
  class: 'C1' | 'C2' | 'C3' | 'C4' | 'C5' | 'C6';
  className: string;
  effect: string;
  width: number;
  height: number;
  islands: Array<{ id: string; x: number; y: number; radius: number; biome: string }>;
  hazards: Array<{ id: string; type: string; tileX: number; tileY: number; radius: number; active: boolean }>;
  exitPoint: { tileX: number; tileY: number; worldX: number; worldY: number; stable: boolean };
  playerCount: number;
}

// Owner type colors for territory
const OWNER_COLORS: Record<string, number> = {
  admin: 0xffd700,           // Gold for permanent locations
  player: 0x3498db,          // Blue for player-owned
  crew: 0xe74c3c,            // Red for crew-owned
  faction_regime: 0xe74c3c,  // Red for regime territories (Arcadia)
  faction_navy: 0x3b82f6,    // Blue for Navy territories
  faction_pirate: 0xef4444,  // Red for Pirate territories
  faction_merchant: 0x10b981, // Green for Merchant territories
  faction_smuggler: 0x8b5cf6, // Purple for Smuggler territories
};

export class WorldMapScene extends Phaser.Scene {
  private socket: Socket | null = null;
  private localPlayerId: string = '';
  private islands: Map<string, IslandSprite> = new Map();
  private loadedChunks: Set<string> = new Set();
  private camera!: Phaser.Cameras.Scene2D.Camera;
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private cameraStartX: number = 0;
  private cameraStartY: number = 0;
  private zoomLevel: number = 1;
  
  // Smooth zoom state
  private isZoomTweening: boolean = false;
  
  // Pan momentum state
  private dragVelocityX: number = 0;
  private dragVelocityY: number = 0;
  private lastPointerX: number = 0;
  private lastPointerY: number = 0;
  private momentumTween: Phaser.Tweens.Tween | null = null;
  private oceanGraphics!: Phaser.GameObjects.Graphics;
  private gridGraphics!: Phaser.GameObjects.Graphics;
  
  // Player location marker
  private playerMarker: Phaser.GameObjects.Container | null = null;
  private playerPosition: { x: number; y: number } = { x: 0, y: 0 };
  private hasCenteredOnPlayer: boolean = false;
  private allowAutoCenterOnPlayer: boolean = true;

  // World config - 8x8 chunks (doubled from 500px to 1000px per chunk)
  private chunkSize: number = 1000;
  private worldChunks: number = 8;
  private worldWidth: number = this.chunkSize * this.worldChunks;  // 8000px
  private worldHeight: number = this.chunkSize * this.worldChunks; // 8000px
  
  // Loading state
  private isLoading: boolean = true;
  private loadingContainer: Phaser.GameObjects.Container | null = null;
  private pendingChunks: Set<string> = new Set();
  private totalChunksToLoad: number = 0;

  private loadingTipIndex: number = 0;
  private loadingTipText: Phaser.GameObjects.Text | null = null;
  private loadingTipTimer: Phaser.Time.TimerEvent | null = null;

  // Scene lifecycle guard: async chunk loads can finish after the scene is destroyed
  private isSceneAlive: boolean = false;

  private canCreateGameObjects(): boolean {
    // Phaser can leave SceneSystems present while display/update lists are already torn down.
    // Guard all async chunk rendering with these checks.
    return Boolean(
      this.isSceneAlive &&
        this.sys &&
        this.sys.displayList &&
        this.sys.updateList
    );
  }
  
  // Destination / Log Pose
  private destinationMarker: Phaser.GameObjects.Container | null = null;
  private destinationCallback: ((x: number, y: number, name?: string) => void) | null = null;
  
  // Coordinate tooltip
  private coordinateTooltip: Phaser.GameObjects.Container | null = null;

  private pointerDownAt: { x: number; y: number } | null = null;

  private hoveredIslandId: string | null = null;
  private territoryUpdateHandler: ((data: { islandId: string; ownerId: string | null; ownerType: string | null; ownerName: string | null; ownerShipColor: string | null; customName: string | null; claimRadius: number | null }) => void) | null = null;
  private hasBoundTerritorySocket: boolean = false;

  private getMinZoomToFitWorld(): number {
    // Fit the full world into the current viewport.
    // Use a slight margin so the full border isn't clipped by rounding.
    const viewportWidth: number = this.scale.width;
    const viewportHeight: number = this.scale.height;
    const extendedWorldWidth: number = this.worldWidth + UNCHARTED_WATERS_BUFFER_PX * 2;
    const extendedWorldHeight: number = this.worldHeight + UNCHARTED_WATERS_BUFFER_PX * 2;
    const zoomToFitX: number = viewportWidth / extendedWorldWidth;
    const zoomToFitY: number = viewportHeight / extendedWorldHeight;
    const zoomToFit: number = Math.min(zoomToFitX, zoomToFitY);
    return Math.max(0.05, zoomToFit);
  }

  // Whirlpool instance mode
  private isInWhirlpool: boolean = false;
  private whirlpoolInstance: WhirlpoolInstanceMapData | null = null;
  private instanceContainer: Phaser.GameObjects.Container | null = null;

  // Rift Storm markers
  private riftStormMarkers: Map<string, {
    graphics: Phaser.GameObjects.Graphics;
    spiral: Phaser.GameObjects.Graphics;
    lightning: Phaser.GameObjects.Graphics;
    clouds: Phaser.GameObjects.Graphics;
    label: Phaser.GameObjects.Text;
    spiralTween: Phaser.Tweens.Tween;
    lightningTimer: Phaser.Time.TimerEvent;
    countdownTimer: Phaser.Time.TimerEvent;
    cloudTween: Phaser.Tweens.Tween;
    expiresAt: number;
    tier: string;
  }> = new Map();

  private pirateRaidsByIslandId: Set<string> = new Set();

  private riftScarMarkers: Map<string, {
    container: Phaser.GameObjects.Container;
    portal: Phaser.GameObjects.Graphics;
    glow: Phaser.GameObjects.Graphics;
    pulseTimer: Phaser.Time.TimerEvent;
    data: { id: string; x: number; y: number; name: string };
  }> = new Map();

  constructor() {
    super({ key: 'WorldMapScene' });
  }

  setSocket(socket: Socket | null): void {
    this.socket = socket;
    this.bindTerritorySocketHandlers();
  }

  setLocalPlayerId(id: string): void {
    this.localPlayerId = id;
  }
  
  setPlayerPosition(x: number, y: number): void {
    this.playerPosition = { x, y };
    this.updatePlayerMarker();

    // Center camera once we have a real position (prevents showing top-left default)
    // Only do this on the very first call, not on every update
    if (this.camera && this.allowAutoCenterOnPlayer && !this.hasCenteredOnPlayer && !(x === 0 && y === 0)) {
      this.camera.centerOn(x, y);
      this.hasCenteredOnPlayer = true;
    }
  }

  setDestinationCallback(callback: (x: number, y: number, name?: string) => void): void {
    this.destinationCallback = callback;
  }

  resetCameraToPlayer(): void {
    if (this.camera) {
      this.camera.centerOn(this.playerPosition.x, this.playerPosition.y);
    }
  }

  /**
   * Zoom in (programmatically) - used by UI zoom controls
   */
  zoomIn(): void {
    if (this.isZoomTweening) return;
    
    const minZoom: number = this.getMinZoomToFitWorld();
    const targetZoom = Phaser.Math.Clamp(this.zoomLevel + 0.2, minZoom, 2);
    
    if (targetZoom === this.zoomLevel) return;
    
    this.performSmoothZoom(targetZoom);
  }

  /**
   * Zoom out (programmatically) - used by UI zoom controls
   */
  zoomOut(): void {
    if (this.isZoomTweening) return;
    
    const minZoom: number = this.getMinZoomToFitWorld();
    const targetZoom = Phaser.Math.Clamp(this.zoomLevel - 0.2, minZoom, 2);
    
    if (targetZoom === this.zoomLevel) return;
    
    this.performSmoothZoom(targetZoom);
  }

  /**
   * Get current zoom level (for UI display)
   */
  getCurrentZoom(): number {
    return this.zoomLevel;
  }

  /**
   * Get min/max zoom bounds (for UI display)
   */
  getZoomBounds(): { min: number; max: number } {
    return {
      min: this.getMinZoomToFitWorld(),
      max: 2
    };
  }

  /**
   * Perform smooth zoom transition centered on current camera view
   */
  private performSmoothZoom(targetZoom: number): void {
    const oldZoom = this.zoomLevel;
    
    // Calculate center of current view
    const centerWorldX = this.camera.scrollX + this.camera.width / 2 / this.camera.zoom;
    const centerWorldY = this.camera.scrollY + this.camera.height / 2 / this.camera.zoom;
    
    // Store initial camera position
    const initialScrollX = this.camera.scrollX;
    const initialScrollY = this.camera.scrollY;
    
    // Mark zoom as active
    this.isZoomTweening = true;
    
    // Smooth zoom transition
    this.tweens.add({
      targets: this.camera,
      zoom: targetZoom,
      duration: 200,
      ease: 'Power2',
      onUpdate: () => {
        // Continuously adjust scroll to keep center position stable
        const currentRatio = this.camera.zoom / oldZoom;
        this.camera.scrollX = centerWorldX - (centerWorldX - initialScrollX) * currentRatio;
        this.camera.scrollY = centerWorldY - (centerWorldY - initialScrollY) * currentRatio;
      },
      onComplete: () => {
        this.zoomLevel = targetZoom;
        this.isZoomTweening = false;
        this.loadVisibleChunks();
      }
    });
  }

  updatePirateRaids(raids: PirateRaidData[]): void {
    this.pirateRaidsByIslandId = new Set(raids.map((r) => r.islandId));
    this.refreshRaidOverlays();

    // Update raid markers on world map
    const currentIds = new Set(raids.map(r => r.id));
    const markersToRemove: string[] = [];
    this.pirateRaidMarkers.forEach((_, id) => {
      if (!currentIds.has(id)) {
        markersToRemove.push(id);
      }
    });
    markersToRemove.forEach(id => this.removePirateRaidMarker(id));

    // Add new markers
    for (const raid of raids) {
      if (!this.pirateRaidMarkers.has(raid.id)) {
        this.addPirateRaidMarker(raid.islandX, raid.islandY, raid.islandName, raid.id);
      }
    }
  }

  private refreshRaidOverlays(): void {
    // Only Trading Posts get the red pulse overlay.
    this.islands.forEach((sprite) => {
      const islandIdLower = sprite.islandData.id.toLowerCase();
      const isTradingPost = sprite.islandData.isPermanent && islandIdLower.includes('trading_post');
      const isUnderRaid = this.pirateRaidsByIslandId.has(sprite.islandData.id);

      if (!isTradingPost) {
        return;
      }

      if (isUnderRaid) {
        this.enableRaidOverlay(sprite);
      } else {
        this.disableRaidOverlay(sprite);
      }
    });
  }

  private enableRaidOverlay(sprite: IslandSprite): void {
    if (!this.canCreateGameObjects()) return;
    if (sprite.raidOverlay) return;

    const overlay = this.add.graphics();
    overlay.setDepth(23);

    const centerTileX = Math.floor(sprite.islandData.x / TILE_SIZE);
    const centerTileY = Math.floor(sprite.islandData.y / TILE_SIZE);

    // Pulse only the marker '+' tiles (grid aesthetic)
    const plusTiles = [
      { dx: 0, dy: -1 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 0 },
      { dx: 1, dy: 0 },
      { dx: 0, dy: 1 },
    ];

    plusTiles.forEach((tile) => {
      const worldX = (centerTileX + tile.dx) * TILE_SIZE;
      const worldY = (centerTileY + tile.dy) * TILE_SIZE;
      overlay.fillStyle(0xff3333, 0.9);
      overlay.fillRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
      overlay.lineStyle(1, 0xaa0000, 1);
      overlay.strokeRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
    });

    overlay.setAlpha(0.25);
    const tween = this.tweens.add({
      targets: overlay,
      alpha: 0.9,
      duration: 450,
      yoyo: true,
      repeat: -1,
    });

    sprite.raidOverlay = overlay;
    sprite.raidTween = tween;
  }

  private disableRaidOverlay(sprite: IslandSprite): void {
    if (sprite.raidTween) {
      sprite.raidTween.stop();
      sprite.raidTween = undefined;
    }
    if (sprite.raidOverlay) {
      sprite.raidOverlay.destroy();
      sprite.raidOverlay = undefined;
    }
  }

  /**
   * Create text with high resolution for sharp rendering when zoomed
   */
  private createSharpText(x: number, y: number, text: string, style: Phaser.Types.GameObjects.Text.TextStyle): Phaser.GameObjects.Text {
    const textObj = this.add.text(x, y, text, {
      ...style,
      fontFamily: '"Sometype Mono", monospace',
    });
    textObj.setResolution(2); // Higher resolution for zoom sharpness
    return textObj;
  }

  showDestinationMarker(x: number, y: number): void {
    // Remove existing marker
    if (this.destinationMarker) {
      this.destinationMarker.destroy();
    }

    // Create destination marker (pulsing target)
    this.destinationMarker = this.add.container(x, y);
    
    // Outer ring
    const outerRing = this.add.graphics();
    outerRing.lineStyle(2, 0xff6b35, 0.8);
    outerRing.strokeCircle(0, 0, 20);
    
    // Inner ring
    const innerRing = this.add.graphics();
    innerRing.lineStyle(2, 0xff6b35, 1);
    innerRing.strokeCircle(0, 0, 10);
    
    // Center dot
    const centerDot = this.add.graphics();
    centerDot.fillStyle(0xff6b35, 1);
    centerDot.fillCircle(0, 0, 4);
    
    // Crosshairs
    const crosshairs = this.add.graphics();
    crosshairs.lineStyle(1, 0xff6b35, 0.6);
    crosshairs.moveTo(-25, 0);
    crosshairs.lineTo(-12, 0);
    crosshairs.moveTo(12, 0);
    crosshairs.lineTo(25, 0);
    crosshairs.moveTo(0, -25);
    crosshairs.lineTo(0, -12);
    crosshairs.moveTo(0, 12);
    crosshairs.lineTo(0, 25);
    crosshairs.strokePath();
    
    // Label
    const label = this.createSharpText(0, 30, 'DESTINATION', {
      fontSize: '10px',
      color: '#ff6b35',
    });
    label.setOrigin(0.5);
    
    this.destinationMarker.add([outerRing, innerRing, centerDot, crosshairs, label]);
    this.destinationMarker.setDepth(100);
    
    // Pulse animation
    this.tweens.add({
      targets: outerRing,
      scaleX: 1.3,
      scaleY: 1.3,
      alpha: 0,
      duration: 1000,
      repeat: -1,
    });
  }

  private showCoordinateTooltip(x: number, y: number, locationName?: string): void {
    // Remove existing tooltip
    if (this.coordinateTooltip) {
      this.coordinateTooltip.destroy();
    }

    // Create tooltip container
    this.coordinateTooltip = this.add.container(x, y - 50);
    this.coordinateTooltip.setDepth(200);

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x0d1117, 0.95);
    bg.lineStyle(2, 0x3fb950, 1);
    
    // Text content
    const coordText = `X: ${x}  Y: ${y}`;
    const locationText = locationName || 'Open Sea';
    
    const coordLabel = this.createSharpText(0, 0, coordText, {
      fontSize: '12px',
      color: '#3fb950',
      fontStyle: 'bold',
    });
    coordLabel.setOrigin(0.5);
    
    const locLabel = this.createSharpText(0, 16, locationText, {
      fontSize: '10px',
      color: '#8b949e',
    });
    locLabel.setOrigin(0.5);
    
    const copyHint = this.createSharpText(0, 32, '(copied to clipboard)', {
      fontSize: '8px',
      color: '#6e7681',
    });
    copyHint.setOrigin(0.5);
    
    // Calculate background size
    const maxWidth = Math.max(coordLabel.width, locLabel.width, copyHint.width) + 20;
    const height = 50;
    
    bg.fillRect(-maxWidth / 2, -10, maxWidth, height);
    bg.strokeRect(-maxWidth / 2, -10, maxWidth, height);
    
    this.coordinateTooltip.add([bg, coordLabel, locLabel, copyHint]);
    
    // Copy coordinates to clipboard
    const clipboardText = locationName 
      ? `${locationName}: (${x}, ${y})`
      : `(${x}, ${y})`;
    navigator.clipboard.writeText(clipboardText).catch(() => {
      console.log('Could not copy to clipboard');
    });
    
    // Fade out after 2 seconds
    this.tweens.add({
      targets: this.coordinateTooltip,
      alpha: 0,
      duration: 500,
      delay: 2000,
      onComplete: () => {
        if (this.coordinateTooltip) {
          this.coordinateTooltip.destroy();
          this.coordinateTooltip = null;
        }
      },
    });
  }

  create(): void {
    this.isSceneAlive = true;
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.isSceneAlive = false;
    });
    this.events.once(Phaser.Scenes.Events.DESTROY, () => {
      this.isSceneAlive = false;
    });

    // Set up camera
    this.camera = this.cameras.main;
    this.camera.setBounds(
      -UNCHARTED_WATERS_BUFFER_PX,
      -UNCHARTED_WATERS_BUFFER_PX,
      this.worldWidth + UNCHARTED_WATERS_BUFFER_PX * 2,
      this.worldHeight + UNCHARTED_WATERS_BUFFER_PX * 2
    );

    // Allow zooming out far enough to view the entire world
    const minZoom: number = this.getMinZoomToFitWorld();
    this.zoomLevel = Phaser.Math.Clamp(this.zoomLevel, minZoom, 2);
    this.camera.setZoom(this.zoomLevel);

    // Default camera position until real player position is set
    this.camera.centerOn(this.worldWidth / 2, this.worldHeight / 2);

    // Draw ocean background
    this.oceanGraphics = this.add.graphics();
    this.drawOcean();

    // Region overlays (no grid lines)
    this.gridGraphics = this.add.graphics();
    this.drawRegions();

    // Set up input
    this.setupInput();

    // Create loading UI
    this.createLoadingUI();
    
    // Load ALL chunks upfront to prevent progressive loading
    this.loadAllChunks();

    // Create player marker
    this.createPlayerMarker();

    this.bindTerritorySocketHandlers();
  }

  private bindTerritorySocketHandlers(): void {
    if (!this.socket) return;
    if (this.hasBoundTerritorySocket) return;

    this.territoryUpdateHandler = (data: { islandId: string; ownerId: string | null; ownerType: string | null; ownerName: string | null; ownerShipColor: string | null; customName: string | null; claimRadius: number | null }): void => {
      const sprite = this.islands.get(data.islandId);
      if (!sprite) return;

      // Update the underlying island data used by tooltips and labels
      sprite.islandData.ownerId = data.ownerId;
      sprite.islandData.ownerType = data.ownerType;
      sprite.islandData.ownerName = data.ownerName;
      sprite.islandData.customName = data.customName;
      sprite.islandData.claimRadius = data.claimRadius;

      // Update territory overlay
      if (sprite.territoryRect) {
        sprite.territoryRect.destroy();
        sprite.territoryRect = undefined;
      }

      if (data.ownerId && data.claimRadius) {
        const radiusTiles = Math.max(2, Math.round(data.claimRadius / TILE_SIZE));
        const size = radiusTiles * TILE_SIZE * 2;

        const ownerKey = (data.ownerType || 'player') as string;
        const color = OWNER_COLORS[ownerKey] ?? OWNER_COLORS.player;

        const rect = this.add.rectangle(sprite.islandData.x, sprite.islandData.y, size, size, color, 0.15);
        rect.setDepth(0);
        sprite.territoryRect = rect;
      }

      // Update name label (claimed islands show customName)
      const displayName = sprite.islandData.customName || sprite.islandData.permanentName;
      if (sprite.nameText) {
        if (displayName) {
          sprite.nameText.setText(displayName);
        } else {
          sprite.nameText.destroy();
          sprite.nameText = undefined;
        }
      } else if (displayName) {
        const radiusTiles = Math.max(2, Math.round(sprite.islandData.radius / TILE_SIZE));
        const nameText = this.createSharpText(sprite.islandData.x, sprite.islandData.y - radiusTiles * TILE_SIZE - 10, displayName, {
          fontSize: '10px',
          color: sprite.islandData.isPermanent ? '#ffd700' : '#ffffff',
          backgroundColor: '#000000aa',
          padding: { x: 4, y: 2 },
        });
        nameText.setOrigin(0.5);
        nameText.setDepth(2);
        sprite.nameText = nameText;
      }

      // If tooltip is currently showing this island, refresh it immediately
      if (this.hoveredIslandId && this.hoveredIslandId === data.islandId) {
        this.showIslandTooltip(sprite.islandData);
      }
    };

    this.socket.on('territory:update', this.territoryUpdateHandler);
    this.hasBoundTerritorySocket = true;

    // Ensure cleanup if the scene is torn down while the socket persists
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      if (this.socket && this.territoryUpdateHandler) {
        this.socket.off('territory:update', this.territoryUpdateHandler);
      }
      this.hasBoundTerritorySocket = false;
      this.territoryUpdateHandler = null;
    });
  }
  
  private createPlayerMarker(): void {
    // Create a pulsing marker for player location
    this.playerMarker = this.add.container(this.playerPosition.x, this.playerPosition.y);
    this.playerMarker.setDepth(150);
    
    // Outer pulse ring
    const pulseRing = this.add.graphics();
    pulseRing.lineStyle(2, 0x00ff00, 0.8);
    pulseRing.strokeRect(-12, -12, 24, 24);
    this.playerMarker.add(pulseRing);
    
    // Inner solid square
    const innerSquare = this.add.graphics();
    innerSquare.fillStyle(0x00ff00, 1);
    innerSquare.fillRect(-6, -6, 12, 12);
    this.playerMarker.add(innerSquare);
    
    // "YOU" label
    const label = this.createSharpText(0, -20, 'YOU', {
      fontSize: '10px',
      color: '#00ff00',
      backgroundColor: '#000000cc',
      padding: { x: 4, y: 2 },
    });
    label.setOrigin(0.5);
    this.playerMarker.add(label);
    
    // Pulse animation
    this.tweens.add({
      targets: pulseRing,
      scaleX: 1.5,
      scaleY: 1.5,
      alpha: 0,
      duration: 1000,
      repeat: -1,
    });
  }
  
  private updatePlayerMarker(): void {
    if (this.playerMarker) {
      this.playerMarker.setPosition(this.playerPosition.x, this.playerPosition.y);
    }
  }
  
  private createLoadingUI(): void {
    // Create loading overlay that covers the screen
    this.loadingContainer = this.add.container(this.worldWidth / 2, this.worldHeight / 2);
    this.loadingContainer.setDepth(200);
    this.loadingContainer.setScrollFactor(0); // Fixed to camera
    
    // Background panel
    const bg = this.add.graphics();
    bg.fillStyle(0x0a1628, 0.95);
    bg.fillRect(-150, -60, 300, 120);
    bg.lineStyle(2, 0x3498db, 1);
    bg.strokeRect(-150, -60, 300, 120);
    this.loadingContainer.add(bg);
    
    // Loading text
    const loadingText = this.createSharpText(0, -20, 'LOADING WORLD MAP', {
      fontSize: '14px',
      color: '#ffffff',
    });
    loadingText.setOrigin(0.5);
    this.loadingContainer.add(loadingText);
    
    // Progress text
    const progressText = this.createSharpText(0, 10, '0 / 64 chunks', {
      fontSize: '12px',
      color: '#94a3b8',
    });
    progressText.setOrigin(0.5);
    progressText.setName('progressText');
    this.loadingContainer.add(progressText);

    // Loading tip text (rotates while loading)
    this.loadingTipIndex = 0;
    this.loadingTipText = this.createSharpText(0, 52, getLoadingTip(this.loadingTipIndex), {
      fontSize: '10px',
      color: '#94a3b8',
      align: 'center',
      wordWrap: { width: 260, useAdvancedWrap: true },
    });
    this.loadingTipText.setOrigin(0.5, 0.5);
    this.loadingTipText.setName('loadingTipText');
    this.loadingContainer.add(this.loadingTipText);

    if (this.loadingTipTimer) {
      this.loadingTipTimer.remove(false);
      this.loadingTipTimer = null;
    }
    this.loadingTipTimer = this.time.addEvent({
      delay: 4500,
      loop: true,
      callback: () => {
        if (!this.loadingTipText || !this.isLoading) return;
        this.loadingTipIndex += 1;
        this.loadingTipText.setText(getLoadingTip(this.loadingTipIndex));
      },
    });
    
    // Progress bar background
    const barBg = this.add.graphics();
    barBg.fillStyle(0x1e293b, 1);
    barBg.fillRect(-100, 30, 200, 12);
    this.loadingContainer.add(barBg);
    
    // Progress bar fill
    const barFill = this.add.graphics();
    barFill.setName('progressBar');
    this.loadingContainer.add(barFill);

    // Ensure timers are cleaned up if the scene is stopped during loading
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      if (this.loadingTipTimer) {
        this.loadingTipTimer.remove(false);
        this.loadingTipTimer = null;
      }
      this.loadingTipText = null;
    });
  }
  
  private updateLoadingProgress(): void {
    if (!this.loadingContainer) return;
    
    const loaded = this.totalChunksToLoad - this.pendingChunks.size;
    const progress = this.totalChunksToLoad > 0 ? loaded / this.totalChunksToLoad : 0;
    
    // Update progress text
    const progressText = this.loadingContainer.getByName('progressText') as Phaser.GameObjects.Text;
    if (progressText) {
      progressText.setText(`${loaded} / ${this.totalChunksToLoad} chunks`);
    }
    
    // Update progress bar
    const barFill = this.loadingContainer.getByName('progressBar') as Phaser.GameObjects.Graphics;
    if (barFill) {
      barFill.clear();
      barFill.fillStyle(0x3498db, 1);
      barFill.fillRect(-100, 30, 200 * progress, 12);
    }
    
    // Hide loading UI when complete
    if (this.pendingChunks.size === 0 && this.isLoading) {
      this.isLoading = false;

      if (this.loadingTipTimer) {
        this.loadingTipTimer.remove(false);
        this.loadingTipTimer = null;
      }

      this.tweens.add({
        targets: this.loadingContainer,
        alpha: 0,
        duration: 300,
        onComplete: () => {
          this.loadingContainer?.destroy();
          this.loadingContainer = null;
          this.loadingTipText = null;
        },
      });
    }
  }
  
  private async loadAllChunks(): Promise<void> {
    if (!this.canCreateGameObjects()) return;

    // Calculate all chunks to load
    const chunksToLoad: { x: number; y: number }[] = [];
    for (let cx = 0; cx < this.worldChunks; cx++) {
      for (let cy = 0; cy < this.worldChunks; cy++) {
        const chunkId = `${cx}_${cy}`;
        if (!this.loadedChunks.has(chunkId)) {
          chunksToLoad.push({ x: cx, y: cy });
          this.pendingChunks.add(chunkId);
        }
      }
    }
    
    this.totalChunksToLoad = chunksToLoad.length;
    
    if (this.totalChunksToLoad === 0) {
      this.isLoading = false;
      this.loadingContainer?.destroy();
      this.loadingContainer = null;
      return;
    }
    
    // Load chunks in parallel batches for speed
    const batchSize = 8;
    for (let i = 0; i < chunksToLoad.length; i += batchSize) {
      if (!this.canCreateGameObjects()) return;
      const batch = chunksToLoad.slice(i, i + batchSize);
      await Promise.all(batch.map(chunk => this.loadChunkWithProgress(chunk.x, chunk.y)));
    }
  }
  
  private async loadChunkWithProgress(chunkX: number, chunkY: number): Promise<void> {
    const chunkId = `${chunkX}_${chunkY}`;
    
    try {
      if (!this.canCreateGameObjects()) return;
      await this.loadChunk(chunkX, chunkY);
    } finally {
      this.pendingChunks.delete(chunkId);
      this.updateLoadingProgress();
    }
  }

  private drawOcean(): void {
    this.oceanGraphics.clear();
    
    // Create ocean gradient effect with multiple layers
    // Deep ocean base
    const extendedX: number = -UNCHARTED_WATERS_BUFFER_PX;
    const extendedY: number = -UNCHARTED_WATERS_BUFFER_PX;
    const extendedWidth: number = this.worldWidth + UNCHARTED_WATERS_BUFFER_PX * 2;
    const extendedHeight: number = this.worldHeight + UNCHARTED_WATERS_BUFFER_PX * 2;

    this.oceanGraphics.fillStyle(UNCHARTED_WATERS_COLOR, 1);
    this.oceanGraphics.fillRect(extendedX, extendedY, extendedWidth, extendedHeight);

    this.oceanGraphics.fillStyle(0x0a1628, 1);
    this.oceanGraphics.fillRect(0, 0, this.worldWidth, this.worldHeight);
    
    // Add subtle wave pattern overlay
    const waveSize = 100;
    for (let y = extendedY; y < extendedY + extendedHeight; y += waveSize) {
      for (let x = extendedX; x < extendedX + extendedWidth; x += waveSize) {
        const noise = Math.sin(x * 0.01) * Math.cos(y * 0.01);
        const alpha = 0.02 + noise * 0.01;
        this.oceanGraphics.fillStyle(0x1e3a5f, Math.abs(alpha));
        this.oceanGraphics.fillRect(x, y, waveSize, waveSize);
      }
    }
    
    // Compass rose (simple, not interactive)
    const compassX = 120;
    const compassY = 120;
    const size = 60;
    
    // Compass background circle
    this.oceanGraphics.fillStyle(0x0d1117, 0.9);
    this.oceanGraphics.fillCircle(compassX, compassY, size + 10);
    this.oceanGraphics.lineStyle(2, 0x3498db, 0.6);
    this.oceanGraphics.strokeCircle(compassX, compassY, size + 10);
    
    // Inner circle
    this.oceanGraphics.lineStyle(1, 0x3498db, 0.4);
    this.oceanGraphics.strokeCircle(compassX, compassY, size - 10);
    
    // Cardinal direction lines
    this.oceanGraphics.lineStyle(2, 0xffd700, 0.8);
    // N-S line
    this.oceanGraphics.moveTo(compassX, compassY - size);
    this.oceanGraphics.lineTo(compassX, compassY + size);
    // E-W line
    this.oceanGraphics.moveTo(compassX - size, compassY);
    this.oceanGraphics.lineTo(compassX + size, compassY);
    this.oceanGraphics.strokePath();
    
    // Diagonal lines
    this.oceanGraphics.lineStyle(1, 0x3498db, 0.4);
    const diag = size * 0.7;
    this.oceanGraphics.moveTo(compassX - diag, compassY - diag);
    this.oceanGraphics.lineTo(compassX + diag, compassY + diag);
    this.oceanGraphics.moveTo(compassX + diag, compassY - diag);
    this.oceanGraphics.lineTo(compassX - diag, compassY + diag);
    this.oceanGraphics.strokePath();
    
    // North arrow (triangle)
    this.oceanGraphics.fillStyle(0xff6b35, 1);
    this.oceanGraphics.beginPath();
    this.oceanGraphics.moveTo(compassX, compassY - size - 5);
    this.oceanGraphics.lineTo(compassX - 8, compassY - size + 15);
    this.oceanGraphics.lineTo(compassX + 8, compassY - size + 15);
    this.oceanGraphics.closePath();
    this.oceanGraphics.fillPath();
    
    // Direction labels
    const labelStyle = { fontSize: '12px', color: '#ffd700' };
    this.createSharpText(compassX, compassY - size - 18, 'N', labelStyle).setOrigin(0.5).setDepth(5);
    this.createSharpText(compassX, compassY + size + 12, 'S', labelStyle).setOrigin(0.5).setDepth(5);
    this.createSharpText(compassX - size - 12, compassY, 'W', labelStyle).setOrigin(0.5).setDepth(5);
    this.createSharpText(compassX + size + 12, compassY, 'E', labelStyle).setOrigin(0.5).setDepth(5);
    
    // Center dot
    this.oceanGraphics.fillStyle(0xffd700, 1);
    this.oceanGraphics.fillCircle(compassX, compassY, 4);

    const labelAlpha: number = 0.22;
    const topLabel = this.createSharpText(this.worldWidth / 2, -UNCHARTED_WATERS_BUFFER_PX / 2, 'UNCHARTED WATERS', {
      fontSize: '18px',
      color: '#7a8aa0',
      fontStyle: 'bold',
    });
    topLabel.setOrigin(0.5);
    topLabel.setAlpha(labelAlpha);
    topLabel.setDepth(5);

    const bottomLabel = this.createSharpText(this.worldWidth / 2, this.worldHeight + UNCHARTED_WATERS_BUFFER_PX / 2, 'UNCHARTED WATERS', {
      fontSize: '18px',
      color: '#7a8aa0',
      fontStyle: 'bold',
    });
    bottomLabel.setOrigin(0.5);
    bottomLabel.setAlpha(labelAlpha);
    bottomLabel.setDepth(5);

    const leftLabel = this.createSharpText(-UNCHARTED_WATERS_BUFFER_PX / 2, this.worldHeight / 2, 'UNCHARTED WATERS', {
      fontSize: '18px',
      color: '#7a8aa0',
      fontStyle: 'bold',
    });
    leftLabel.setOrigin(0.5);
    leftLabel.setRotation(-Math.PI / 2);
    leftLabel.setAlpha(labelAlpha);
    leftLabel.setDepth(5);

    const rightLabel = this.createSharpText(this.worldWidth + UNCHARTED_WATERS_BUFFER_PX / 2, this.worldHeight / 2, 'UNCHARTED WATERS', {
      fontSize: '18px',
      color: '#7a8aa0',
      fontStyle: 'bold',
    });
    rightLabel.setOrigin(0.5);
    rightLabel.setRotation(Math.PI / 2);
    rightLabel.setAlpha(labelAlpha);
    rightLabel.setDepth(5);
  }

  private drawRegions(): void {
    this.gridGraphics.clear();
    
    // Draw world boundary only - no grid lines
    const borderWidth = 3;
    this.gridGraphics.lineStyle(borderWidth, 0xe74c3c, 0.8);
    this.gridGraphics.strokeRect(
      borderWidth / 2, 
      borderWidth / 2, 
      this.worldWidth - borderWidth, 
      this.worldHeight - borderWidth
    );

    // Draw region labels matching actual NPC spawn regions
    // World is 8000x8000 (8x8 chunks of 1000px each)
    // Layout: '+' pattern for cardinal Reaches, corners have unique biomes, Arcadia at center
    const regions = [
      // Cardinal directions ('+' pattern around Arcadia)
      { name: 'NORTHERN REACH', x: 3500, y: 1000, color: '#1abc9c', desc: 'Frozen Waters' },
      { name: 'SOUTHERN REACH', x: 3500, y: 6500, color: '#e67e22', desc: 'Tropical Seas' },
      { name: 'EASTERN REACH', x: 6500, y: 3500, color: '#3498db', desc: 'Trade Routes' },
      { name: 'WESTERN REACH', x: 1000, y: 3500, color: '#9b59b6', desc: 'Uncharted Waters' },
      // Corner regions (new biomes/themes)
      { name: 'FROSTWIND ISLES', x: 1000, y: 1000, color: '#a8d8ea', desc: 'Icy Archipelago' },
      { name: 'SUNFIRE ARCHIPELAGO', x: 6500, y: 1000, color: '#ff6b6b', desc: 'Volcanic Islands' },
      { name: 'STORMVEIL DEPTHS', x: 1000, y: 6500, color: '#4a4e69', desc: 'Stormy Abyss' },
      { name: 'GOLDEN SHOALS', x: 6500, y: 6500, color: '#ffd93d', desc: 'Treasure Waters' },
      // Center region
      { name: 'ARCADIA', x: 4000, y: 4000, color: '#e74c3c', desc: 'Elite Pirates & Commanders' },
    ];

    // Draw region boundaries (faded rectangles)
    // '+' pattern: cardinal reaches in center strips, corners have unique biomes
    const regionBounds = [
      // Cardinal directions ('+' pattern)
      { minX: 3000, maxX: 5000, minY: 0, maxY: 3000, color: 0x1abc9c },       // Northern Reach (top-center)
      { minX: 3000, maxX: 5000, minY: 5000, maxY: 8000, color: 0xe67e22 },    // Southern Reach (bottom-center)
      { minX: 5000, maxX: 8000, minY: 3000, maxY: 5000, color: 0x3498db },    // Eastern Reach (center-right)
      { minX: 0, maxX: 3000, minY: 3000, maxY: 5000, color: 0x9b59b6 },       // Western Reach (center-left)
      // Corner regions
      { minX: 0, maxX: 3000, minY: 0, maxY: 3000, color: 0xa8d8ea },          // Frostwind Isles (NW)
      { minX: 5000, maxX: 8000, minY: 0, maxY: 3000, color: 0xff6b6b },       // Sunfire Archipelago (NE)
      { minX: 0, maxX: 3000, minY: 5000, maxY: 8000, color: 0x4a4e69 },       // Stormveil Depths (SW)
      { minX: 5000, maxX: 8000, minY: 5000, maxY: 8000, color: 0xffd93d },    // Golden Shoals (SE)
      // Center
      { minX: 3000, maxX: 5000, minY: 3000, maxY: 5000, color: 0xe74c3c },    // Arcadia (center)
    ];

    regionBounds.forEach((bounds) => {
      // Bolder region fill - more visible
      this.gridGraphics.fillStyle(bounds.color, 0.12);
      this.gridGraphics.fillRect(
        bounds.minX, bounds.minY,
        bounds.maxX - bounds.minX, bounds.maxY - bounds.minY
      );
      // Subtle border
      this.gridGraphics.lineStyle(2, bounds.color, 0.4);
      this.gridGraphics.strokeRect(
        bounds.minX, bounds.minY,
        bounds.maxX - bounds.minX, bounds.maxY - bounds.minY
      );
    });

    // Draw region labels
    regions.forEach((region) => {
      // Region name
      const nameText = this.createSharpText(region.x, region.y, region.name, {
        fontSize: '14px',
        color: region.color,
        fontStyle: 'bold',
        backgroundColor: '#000000aa',
        padding: { x: 4, y: 2 },
      });
      nameText.setOrigin(0.5).setDepth(10);
      
      // Region description (NPC types)
      const descText = this.createSharpText(region.x, region.y + 18, region.desc, {
        fontSize: '9px',
        color: '#888888',
        backgroundColor: '#000000aa',
        padding: { x: 3, y: 1 },
      });
      descText.setOrigin(0.5).setDepth(10);
    });
    
    // Draw danger level indicators matching new region layout
    const dangerZones = [
      // Corner regions (starter areas - Low danger)
      { x: 1500, y: 1500, level: 'DANGER: Low', color: '#2ecc71' },    // Frostwind Isles (NW)
      { x: 6500, y: 1500, level: 'DANGER: Low', color: '#2ecc71' },    // Sunfire Archipelago (NE)
      { x: 1500, y: 6500, level: 'DANGER: Low', color: '#2ecc71' },    // Stormveil Depths (SW)
      { x: 6500, y: 6500, level: 'DANGER: Low', color: '#2ecc71' },    // Golden Shoals (SE)
      // Cardinal reaches (Medium danger)
      { x: 4000, y: 1500, level: 'DANGER: Medium', color: '#f39c12' }, // Northern Reach
      { x: 4000, y: 6500, level: 'DANGER: Medium', color: '#f39c12' }, // Southern Reach
      { x: 1500, y: 4000, level: 'DANGER: Medium', color: '#f39c12' }, // Western Reach
      { x: 6500, y: 4000, level: 'DANGER: Medium', color: '#f39c12' }, // Eastern Reach
      // Center (High danger)
      { x: 4000, y: 4000, level: 'DANGER: High', color: '#e74c3c' },   // Arcadia
    ];
    
    dangerZones.forEach((zone) => {
      const dangerText = this.createSharpText(zone.x, zone.y + 40, zone.level, {
        fontSize: '8px',
        color: zone.color,
      });
      dangerText.setOrigin(0.5).setDepth(10);
    });
    
    // Draw central Latitude and Longitude lines (center of world = 2000, 2000)
    const centerX = this.worldWidth / 2;
    const centerY = this.worldHeight / 2;
    
    // Longitude line (vertical through center)
    this.gridGraphics.lineStyle(2, 0xffd700, 0.6);
    this.gridGraphics.moveTo(centerX, 0);
    this.gridGraphics.lineTo(centerX, this.worldHeight);
    this.gridGraphics.strokePath();
    
    // Latitude line (horizontal through center)
    this.gridGraphics.lineStyle(2, 0xffd700, 0.6);
    this.gridGraphics.moveTo(0, centerY);
    this.gridGraphics.lineTo(this.worldWidth, centerY);
    this.gridGraphics.strokePath();
    
    // Longitude label (top)
    const longitudeLabel = this.createSharpText(centerX, 25, '-- LONGITUDE --', {
      fontSize: '11px',
      color: '#ffd700',
      backgroundColor: '#000000cc',
      padding: { x: 6, y: 3 },
    });
    longitudeLabel.setOrigin(0.5);
    longitudeLabel.setDepth(15);
    
    // Longitude label (bottom)
    const longitudeLabelBottom = this.createSharpText(centerX, this.worldHeight - 25, '-- LONGITUDE --', {
      fontSize: '11px',
      color: '#ffd700',
      backgroundColor: '#000000cc',
      padding: { x: 6, y: 3 },
    });
    longitudeLabelBottom.setOrigin(0.5);
    longitudeLabelBottom.setDepth(15);
    
    // Latitude label (left)
    const latitudeLabel = this.createSharpText(25, centerY, 'LATITUDE', {
      fontSize: '11px',
      color: '#ffd700',
      backgroundColor: '#000000cc',
      padding: { x: 6, y: 3 },
    });
    latitudeLabel.setOrigin(0, 0.5);
    latitudeLabel.setDepth(15);
    
    // Latitude label (right)
    const latitudeLabelRight = this.createSharpText(this.worldWidth - 25, centerY, 'LATITUDE', {
      fontSize: '11px',
      color: '#ffd700',
      backgroundColor: '#000000cc',
      padding: { x: 6, y: 3 },
    });
    latitudeLabelRight.setOrigin(1, 0.5);
    latitudeLabelRight.setDepth(15);
    
    // Center point marker (intersection)
    this.gridGraphics.fillStyle(0xffd700, 1);
    this.gridGraphics.fillRect(centerX - 4, centerY - 4, 8, 8);
    this.gridGraphics.lineStyle(1, 0x000000, 1);
    this.gridGraphics.strokeRect(centerX - 4, centerY - 4, 8, 8);
    
    // Center label
    const centerLabel = this.createSharpText(centerX, centerY - 20, '0, 0', {
      fontSize: '10px',
      color: '#ffd700',
      backgroundColor: '#000000cc',
      padding: { x: 4, y: 2 },
    });
    centerLabel.setOrigin(0.5);
    centerLabel.setDepth(15);
  }

  private setupInput(): void {
    // Pan with mouse drag
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) return;
      this.pointerDownAt = { x: pointer.x, y: pointer.y };
      this.isDragging = true;
      this.dragStartX = pointer.x;
      this.dragStartY = pointer.y;
      this.cameraStartX = this.camera.scrollX;
      this.cameraStartY = this.camera.scrollY;
      
      // Cancel any existing momentum tween
      if (this.momentumTween) {
        this.momentumTween.stop();
        this.momentumTween = null;
      }
      
      // Reset velocity tracking
      this.lastPointerX = pointer.x;
      this.lastPointerY = pointer.y;
      this.dragVelocityX = 0;
      this.dragVelocityY = 0;
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.isDragging) return;
      
      // Track velocity for momentum
      this.dragVelocityX = pointer.x - this.lastPointerX;
      this.dragVelocityY = pointer.y - this.lastPointerY;
      this.lastPointerX = pointer.x;
      this.lastPointerY = pointer.y;
      
      const dx = (this.dragStartX - pointer.x) / this.camera.zoom;
      const dy = (this.dragStartY - pointer.y) / this.camera.zoom;
      this.camera.scrollX = this.cameraStartX + dx;
      this.camera.scrollY = this.cameraStartY + dy;
    });

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      const wasDragging = this.isDragging;
      this.isDragging = false;

      if (pointer.rightButtonDown()) return;
      if (!this.pointerDownAt) return;

      const dragDistance = Phaser.Math.Distance.Between(this.pointerDownAt.x, this.pointerDownAt.y, pointer.x, pointer.y);
      this.pointerDownAt = null;

      // Apply momentum if dragging with sufficient velocity
      if (wasDragging && dragDistance > 6) {
        this.applyMomentum(this.dragVelocityX, this.dragVelocityY);
        return;
      }

      // Treat small movement as a click (select destination). Larger movement is pan.
      if (dragDistance > 6) return;

      const worldX = Math.round(pointer.worldX);
      const worldY = Math.round(pointer.worldY);

      const isInsideWorld: boolean = worldX >= 0 && worldX <= this.worldWidth && worldY >= 0 && worldY <= this.worldHeight;
      if (!isInsideWorld) return;

      // Check if clicked on an island
      let clickedIslandName: string | undefined;
      const islandArray = Array.from(this.islands.values());
      for (const island of islandArray) {
        const dist = Phaser.Math.Distance.Between(worldX, worldY, island.x, island.y);
        if (dist < island.islandData.radius + 20) {
          clickedIslandName =
            island.islandData.permanentName ||
            island.islandData.customName ||
            island.islandData.ownerName ||
            'Unknown Island';
          break;
        }
      }

      // Set destination preview
      if (this.destinationCallback) {
        this.allowAutoCenterOnPlayer = false;
        this.destinationCallback(worldX, worldY, clickedIslandName);
        this.showDestinationMarker(worldX, worldY);
      }
    });

    // Prevent context menu on right-click
    this.input.mouse?.disableContextMenu();

    // Zoom with mouse wheel
    this.input.on('wheel', (pointer: Phaser.Input.Pointer, _gameObjects: unknown[], _deltaX: number, deltaY: number) => {
      // Prevent zoom spam while tween is active
      if (this.isZoomTweening) return;
      
      const minZoom: number = this.getMinZoomToFitWorld();
      const oldZoom = this.zoomLevel;
      const zoomChange = deltaY > 0 ? -0.1 : 0.1;
      const targetZoom = Phaser.Math.Clamp(this.zoomLevel + zoomChange, minZoom, 2);
      
      // No change needed
      if (targetZoom === this.zoomLevel) return;
      
      // Calculate the world point under the pointer before zoom
      const worldX = pointer.worldX;
      const worldY = pointer.worldY;
      
      // Store initial camera position for smooth scroll adjustment
      const initialScrollX = this.camera.scrollX;
      const initialScrollY = this.camera.scrollY;
      
      // Mark zoom as active
      this.isZoomTweening = true;
      
      // Smooth zoom transition
      this.tweens.add({
        targets: this.camera,
        zoom: targetZoom,
        duration: 200,
        ease: 'Power2',
        onUpdate: () => {
          // Continuously adjust scroll to keep pointer position stable
          const currentRatio = this.camera.zoom / oldZoom;
          this.camera.scrollX = worldX - (worldX - initialScrollX) * currentRatio;
          this.camera.scrollY = worldY - (worldY - initialScrollY) * currentRatio;
        },
        onComplete: () => {
          this.zoomLevel = targetZoom;
          this.isZoomTweening = false;
          this.loadVisibleChunks();
        }
      });
    });

    // Keyboard controls
    if (this.input.keyboard) {
      const cursors = this.input.keyboard.createCursorKeys();
      this.input.keyboard.on('keydown', () => {
        const speed = 10 / this.camera.zoom;
        if (cursors.left?.isDown) this.camera.scrollX -= speed;
        if (cursors.right?.isDown) this.camera.scrollX += speed;
        if (cursors.up?.isDown) this.camera.scrollY -= speed;
        if (cursors.down?.isDown) this.camera.scrollY += speed;
      });
    }
  }

  private loadVisibleChunks(): void {
    const bounds = this.camera.worldView;
    const startChunkX = Math.floor(bounds.x / this.chunkSize);
    const startChunkY = Math.floor(bounds.y / this.chunkSize);
    const endChunkX = Math.ceil((bounds.x + bounds.width) / this.chunkSize);
    const endChunkY = Math.ceil((bounds.y + bounds.height) / this.chunkSize);

    const visibleChunks = new Set<string>();

    for (let cx = startChunkX; cx <= endChunkX; cx++) {
      for (let cy = startChunkY; cy <= endChunkY; cy++) {
        if (cx >= 0 && cy >= 0 && cx < this.worldChunks && cy < this.worldChunks) {
          const chunkId = `${cx}_${cy}`;
          visibleChunks.add(chunkId);
          if (!this.loadedChunks.has(chunkId)) {
            this.loadChunk(cx, cy);
          }
        }
      }
    }

    // Unload chunks that are no longer visible (with buffer)
    const chunksToUnload: string[] = [];
    Array.from(this.loadedChunks).forEach(chunkId => {
      if (!visibleChunks.has(chunkId)) {
        const [cx, cy] = chunkId.split('_').map(Number);
        // Add 1 chunk buffer before unloading
        const inBuffer = cx >= startChunkX - 1 && cx <= endChunkX + 1 &&
                         cy >= startChunkY - 1 && cy <= endChunkY + 1;
        if (!inBuffer) {
          chunksToUnload.push(chunkId);
        }
      }
    });

    for (const chunkId of chunksToUnload) {
      this.unloadChunk(chunkId);
    }
  }

  private unloadChunk(chunkId: string): void {
    // Find and destroy all islands in this chunk
    const islandsToRemove: string[] = [];
    
    this.islands.forEach((sprite, islandId) => {
      if (sprite.chunkId === chunkId) {
        islandsToRemove.push(islandId);
      }
    });

    for (const islandId of islandsToRemove) {
      const sprite = this.islands.get(islandId);
      if (!sprite) continue;

      // Destroy associated graphics and labels
      sprite.raidTween?.stop();
      sprite.raidOverlay?.destroy();
      sprite.graphics?.destroy();
      sprite.icon?.destroy();
      sprite.nameText?.destroy();
      sprite.territoryRect?.destroy();
      sprite.labels?.forEach(label => label.destroy());
      sprite.destroy();
      this.islands.delete(islandId);
    }

    // Mark chunk as unloaded so it can be reloaded when visible again
    this.loadedChunks.delete(chunkId);
  }

  private async loadChunk(chunkX: number, chunkY: number): Promise<void> {
    const chunkId = `${chunkX}_${chunkY}`;
    this.loadedChunks.add(chunkId);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/world/chunk/${chunkX}/${chunkY}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }
      const data = await response.json();

      if (!this.canCreateGameObjects()) return;

      if (data.islands) {
        data.islands.forEach((island: Island) => {
          this.createIslandSprite(island, chunkId);
        });
      }
    } catch (error) {
      console.error(`Failed to load chunk ${chunkId}:`, error);
      this.loadedChunks.delete(chunkId); // Allow retry
    }
  }

  private createIslandSprite(island: Island, chunkId: string): void {
    if (!this.canCreateGameObjects()) return;
    if (this.islands.has(island.id)) return;

    // Check if this is a Trading Post or Shipyard - render differently
    // Check island ID since names like "Coral Exchange" don't contain "trading"
    const islandIdLower = island.id.toLowerCase();
    if (
      island.isPermanent &&
      (islandIdLower.includes('trading_post') ||
        islandIdLower.includes('shipyard') ||
        islandIdLower.includes('harbor_office') ||
        islandIdLower.includes('tavern'))
    ) {
      this.createTradingPostMarker(island, chunkId);
      return;
    }

    // Biome-specific color palettes (same as OceanScene)
    const BIOME_PALETTE: Record<string, { shore: number; main: number; accent: number }> = {
      beach:    { shore: 0xf5deb3, main: 0xc2b280, accent: 0xd4c896 },
      forest:   { shore: 0xc2b280, main: 0x228b22, accent: 0x2e8b2e },
      mountain: { shore: 0x8b8b83, main: 0x696969, accent: 0x808080 },
      jungle:   { shore: 0xc2b280, main: 0x006400, accent: 0x228b22 },
      desert:   { shore: 0xf4a460, main: 0xdaa520, accent: 0xcd853f },
      volcanic: { shore: 0x2f2f2f, main: 0x1a1a1a, accent: 0x3d3d3d },
      winter:   { shore: 0xe0e0e0, main: 0xfffafa, accent: 0xf0f8ff },
      mixed:    { shore: 0xc2b280, main: 0x4a7c23, accent: 0x5a8c33 },
    };
    
    const biomeKey = island.biome || 'mixed';
    const colors = BIOME_PALETTE[biomeKey] || BIOME_PALETTE.mixed;
    
    // Island size in tiles
    const radiusTiles = Math.max(2, Math.round(island.radius / TILE_SIZE));
    
    // Create territory rectangle first (behind island)
    let territoryRect: Phaser.GameObjects.Rectangle | undefined;
    if (island.claimRadius && island.ownerId) {
      const ownerColor = OWNER_COLORS[island.ownerType || 'player'] || 0x3498db;
      const territorySize = Math.round(island.claimRadius / TILE_SIZE) * TILE_SIZE * 2;
      territoryRect = this.add.rectangle(island.x, island.y, territorySize, territorySize);
      territoryRect.setStrokeStyle(2, ownerColor, 0.5);
      territoryRect.setFillStyle(ownerColor, 0.1);
      territoryRect.setDepth(0);
    }

    // Create island using graphics - use actual shape data if available
    const graphics = this.add.graphics();
    graphics.setDepth(1);
    
    // Center tile coordinates
    const centerTileX = Math.floor(island.x / TILE_SIZE);
    const centerTileY = Math.floor(island.y / TILE_SIZE);
    
    // Seeded random for consistent tile variation
    const seed = island.x * 1000 + island.y;
    const seededRandom = (offset: number): number => {
      const x = Math.sin(seed + offset) * 10000;
      return x - Math.floor(x);
    };
    
    // GRID-BASED PIXEL ART RENDERING - 1 pixel = 1 tile (TILE_SIZE x TILE_SIZE)
    // Use actual polygon shape if available, otherwise fall back to radius-based
    if (island.shape && island.shape.length >= 3) {
      // Render using actual polygon shape - grid aligned tiles
      const worldPoly = island.shape.map(p => ({ x: island.x + p.x, y: island.y + p.y }));
      const minX = Math.min(...worldPoly.map(p => p.x));
      const maxX = Math.max(...worldPoly.map(p => p.x));
      const minY = Math.min(...worldPoly.map(p => p.y));
      const maxY = Math.max(...worldPoly.map(p => p.y));
      
      const startTileX = Math.floor(minX / TILE_SIZE);
      const endTileX = Math.ceil(maxX / TILE_SIZE);
      const startTileY = Math.floor(minY / TILE_SIZE);
      const endTileY = Math.ceil(maxY / TILE_SIZE);
      
      for (let ty = startTileY; ty <= endTileY; ty++) {
        for (let tx = startTileX; tx <= endTileX; tx++) {
          const centerX = tx * TILE_SIZE + TILE_SIZE / 2;
          const centerY = ty * TILE_SIZE + TILE_SIZE / 2;
          if (this.pointInPolygon(centerX, centerY, worldPoly)) {
            const distFromCenter = Math.sqrt((centerX - island.x) ** 2 + (centerY - island.y) ** 2);
            const maxDist = Math.max(...worldPoly.map(p => Math.sqrt((p.x - island.x) ** 2 + (p.y - island.y) ** 2)));
            const edgeRatio = distFromCenter / maxDist;
            
            let color: number;
            if (edgeRatio > 0.85) {
              color = colors.shore;
            } else {
              color = seededRandom(tx * 30 + ty * 30) > 0.5 ? colors.main : colors.accent;
            }
            
            graphics.fillStyle(color, 1);
            graphics.fillRect(tx * TILE_SIZE, ty * TILE_SIZE, TILE_SIZE, TILE_SIZE);
          }
        }
      }
    } else {
      // Fallback: radius-based grid rendering
      for (let dy = -radiusTiles; dy <= radiusTiles; dy++) {
        for (let dx = -radiusTiles; dx <= radiusTiles; dx++) {
          const dist = Math.sqrt(dx * dx + dy * dy);
          const noise = seededRandom(dx * 100 + dy) * 0.5;
          const threshold = radiusTiles * (0.8 + noise * 0.4);
          
          if (dist <= threshold) {
            const edgeDist = threshold - dist;
            let color: number;
            
            if (edgeDist < 1.2) {
              color = colors.shore;
            } else {
              color = seededRandom(dx * 30 + dy * 30) > 0.5 ? colors.main : colors.accent;
            }
            
            const worldX = (centerTileX + dx) * TILE_SIZE;
            const worldY = (centerTileY + dy) * TILE_SIZE;
            
            graphics.fillStyle(color, 1);
            graphics.fillRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
          }
        }
      }
    }
    
    // Create invisible hitbox for interaction
    const hitboxSize = radiusTiles * TILE_SIZE * 2;
    const sprite = this.add.rectangle(island.x, island.y, hitboxSize, hitboxSize, 0x000000, 0) as unknown as IslandSprite;
    sprite.islandData = island;
    sprite.territoryRect = territoryRect;
    sprite.graphics = graphics;
    sprite.chunkId = chunkId;
    sprite.setDepth(2);

    // No glow rectangles - permanent locations are marked by name labels only

    // Make interactive
    sprite.setInteractive();
    sprite.on('pointerover', () => {
      this.hoveredIslandId = island.id;
      this.showIslandTooltip(island);
    });
    sprite.on('pointerout', () => {
      this.hoveredIslandId = null;
      this.hideIslandTooltip();
    });
    // Add name label for permanent or claimed islands
    const displayName = island.customName || island.permanentName;
    if (displayName) {
      const nameText = this.createSharpText(island.x, island.y - radiusTiles * TILE_SIZE - 10, displayName, {
        fontSize: '10px',
        color: island.isPermanent ? '#ffd700' : '#ffffff',
        backgroundColor: '#000000aa',
        padding: { x: 4, y: 2 },
      });
      nameText.setOrigin(0.5);
      nameText.setDepth(2);
      sprite.nameText = nameText;
    }

    this.islands.set(island.id, sprite);
  }

  /**
   * Create a distinct Trading Post or Shipyard marker on the world map
   */
  private createTradingPostMarker(island: Island, chunkId: string): void {
    const graphics = this.add.graphics();
    graphics.setDepth(20);
    
    const centerTileX = Math.floor(island.x / TILE_SIZE);
    const centerTileY = Math.floor(island.y / TILE_SIZE);
    
    // Determine permanent location type for marker styling
    const islandIdLower = island.id.toLowerCase();
    const isShipyard: boolean = islandIdLower.includes('shipyard');
    const isHarborOffice: boolean = islandIdLower.includes('harbor_office');
    const isTavern: boolean = islandIdLower.includes('tavern');

    const markerColor: number = isShipyard ? 0xffa500 : isHarborOffice ? 0x5dade2 : isTavern ? 0x8b5a2b : 0x87ceeb;
    const markerColorHex: string = isShipyard ? '#ffa500' : isHarborOffice ? '#5dade2' : isTavern ? '#8b5a2b' : '#87ceeb';
    const iconText: string = isShipyard ? '[S]' : isHarborOffice ? '[H]' : isTavern ? '[T]' : '[P]';
    
    // Draw safe zone for hubs that are intended to be safe (not taverns)
    if (!isTavern) {
      const safeRadius: number = 10; // 10 tile radius
      for (let dy = -safeRadius; dy <= safeRadius; dy++) {
        for (let dx = -safeRadius; dx <= safeRadius; dx++) {
          const dist = Math.max(Math.abs(dx), Math.abs(dy));
          if (dist <= safeRadius) {
            const alpha = 0.08 * (1 - dist / safeRadius);
            graphics.fillStyle(markerColor, alpha);
            graphics.fillRect(
              (centerTileX + dx) * TILE_SIZE,
              (centerTileY + dy) * TILE_SIZE,
              TILE_SIZE,
              TILE_SIZE
            );
          }
        }
      }
    }
    
    // Draw marker + shape
    const plusTiles = [
      { dx: 0, dy: -1 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 0 },
      { dx: 1, dy: 0 },
      { dx: 0, dy: 1 },
    ];
    
    plusTiles.forEach(tile => {
      const worldX = (centerTileX + tile.dx) * TILE_SIZE;
      const worldY = (centerTileY + tile.dy) * TILE_SIZE;
      graphics.fillStyle(markerColor, 1);
      graphics.fillRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
      graphics.lineStyle(1, isShipyard ? 0xcc8400 : 0x5dade2, 1);
      graphics.strokeRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
    });
    
    // Add icon
    const icon = this.createSharpText(island.x, island.y, iconText, { fontSize: '10px', color: markerColorHex });
    icon.setOrigin(0.5);
    icon.setDepth(21);
    
    // Add name label
    const displayName: string =
      island.permanentName ||
      (isShipyard ? 'Shipyard' : isHarborOffice ? 'Harbor Office' : isTavern ? 'Tavern' : 'Trading Post');
    const label = this.createSharpText(island.x, island.y + TILE_SIZE * 2, displayName, {
      fontSize: '9px',
      color: markerColorHex,
      backgroundColor: '#000000cc',
      padding: { x: 3, y: 1 },
    });
    label.setOrigin(0.5);
    label.setDepth(21);
    
    // Add "SAFE ZONE" label (not taverns)
    let safeLabel: Phaser.GameObjects.Text | undefined;
    if (!isTavern) {
      safeLabel = this.createSharpText(island.x, island.y - TILE_SIZE * 2, 'SAFE ZONE', {
        fontSize: '8px',
        color: '#2ecc71',
        backgroundColor: '#000000cc',
        padding: { x: 2, y: 1 },
      });
      safeLabel.setOrigin(0.5);
      safeLabel.setDepth(21);
    }
    
    // Create invisible hitbox for interaction
    const hitboxSize = TILE_SIZE * 3;
    const sprite = this.add.rectangle(island.x, island.y, hitboxSize, hitboxSize, 0x000000, 0) as unknown as IslandSprite;
    sprite.islandData = island;
    sprite.graphics = graphics;
    sprite.icon = icon;
    sprite.labels = safeLabel ? [label, safeLabel] : [label];
    sprite.chunkId = chunkId;
    sprite.setDepth(22);
    sprite.setInteractive();
    
    sprite.on('pointerover', () => {
      this.showIslandTooltip(island);
    });
    sprite.on('pointerout', () => {
      this.hideIslandTooltip();
    });
    
    this.islands.set(island.id, sprite);
  }

  private showIslandTooltip(island: Island): void {
    // Create hover tooltip panel
    if (this.hoverTooltip) {
      this.hoverTooltip.destroy();
    }
    
    // Better display name - capitalize type, don't show "small Island"
    const typeName = island.type.charAt(0).toUpperCase() + island.type.slice(1);
    const displayName = island.customName || island.permanentName || `${typeName} Isle`;
    
    // Determine ownership status
    let ownerText = '[UNCLAIMED]';
    let ownerColor = '#95a5a6';
    if (island.isPermanent) {
      ownerText = 'Owner: The Regime';
      ownerColor = '#ffd700';
    } else if (island.ownerId) {
      if (island.ownerType === 'faction_regime') {
        ownerText = `Owner: ${island.ownerName || 'The Regime'}`;
        ownerColor = '#e74c3c';
      } else if (island.ownerType === 'faction_navy') {
        ownerText = `Owner: ${island.ownerName || 'Navy'}`;
        ownerColor = '#3b82f6';
      } else if (island.ownerType === 'faction_pirate') {
        ownerText = `Owner: ${island.ownerName || 'Pirates'}`;
        ownerColor = '#ef4444';
      } else if (island.ownerType === 'faction_merchant') {
        ownerText = `Owner: ${island.ownerName || 'Merchant Guild'}`;
        ownerColor = '#10b981';
      } else if (island.ownerType === 'faction_smuggler') {
        ownerText = `Owner: ${island.ownerName || 'Smugglers'}`;
        ownerColor = '#8b5cf6';
      } else if (island.ownerType === 'crew') {
        ownerText = `Owner: [FLEET] ${island.ownerName || 'Fleet Territory'}`;
        ownerColor = '#e74c3c';
      } else {
        // Player-owned - show name or "Player Territory" if name missing
        ownerText = `Owner: ${island.ownerName || 'Player Territory'}`;
        ownerColor = '#3498db';
      }
    }
    
    // Build tooltip content
    const lines = [
      displayName,
      `Type: ${island.type.charAt(0).toUpperCase() + island.type.slice(1)}`,
      `Biome: ${island.biome.charAt(0).toUpperCase() + island.biome.slice(1)}`,
      ownerText,
    ];
    
    // Create container at island position
    this.hoverTooltip = this.add.container(island.x, island.y - 60);
    this.hoverTooltip.setDepth(200);
    
    // Background
    const padding = 8;
    const lineHeight = 16;
    const width = 160;
    const height = lines.length * lineHeight + padding * 2;
    
    const bg = this.add.rectangle(0, 0, width, height, 0x000000, 0.9);
    bg.setStrokeStyle(1, island.isPermanent ? 0xffd700 : 0x4a5568);
    this.hoverTooltip.add(bg);
    
    // Text lines
    lines.forEach((line, i) => {
      const isTitle = i === 0;
      const isOwner = i === lines.length - 1;
      const text = this.createSharpText(
        -width / 2 + padding,
        -height / 2 + padding + i * lineHeight,
        line,
        {
          fontSize: isTitle ? '11px' : '9px',
          color: isTitle ? '#ffffff' : (isOwner ? ownerColor : '#a0aec0'),
          fontStyle: isTitle ? 'bold' : 'normal',
        }
      );
      if (this.hoverTooltip) {
        this.hoverTooltip.add(text);
      }
    });
  }

  private hideIslandTooltip(): void {
    if (this.hoverTooltip) {
      this.hoverTooltip.destroy();
      this.hoverTooltip = null;
    }
  }
  
  private hoverTooltip: Phaser.GameObjects.Container | null = null;

  // ============ RIFT STORM MARKERS ============

  private formatCountdownSeconds(totalSeconds: number): string {
    const clamped: number = Math.max(0, totalSeconds);
    const minutes: number = Math.floor(clamped / 60);
    const seconds: number = clamped % 60;
    const mm: string = String(minutes).padStart(2, '0');
    const ss: string = String(seconds).padStart(2, '0');
    return `${mm}:${ss}`;
  }

  private updateRiftStormLabel(id: string): void {
    const marker = this.riftStormMarkers.get(id);
    if (!marker) return;
    const remainingSeconds = Math.max(0, Math.ceil((marker.expiresAt - Date.now()) / 1000));
    const tierLabel = marker.tier.toUpperCase();
    const timerText = remainingSeconds > 0 ? this.formatCountdownSeconds(remainingSeconds) : '00:00';
    marker.label.setText(`[RIFT - ${tierLabel}] ${timerText}`);
  }

  private createRiftSpiral(graphics: Phaser.GameObjects.Graphics, radius: number, colorNum: number, rotation: number = 0, wobbleTime: number = 0): void {
    graphics.clear();

    // Draw multiple jagged spiral arms
    const numArms = 3;
    for (let arm = 0; arm < numArms; arm++) {
      const armOffset = (arm / numArms) * Math.PI * 2;
      const alpha = 0.5 + Math.sin(wobbleTime * 0.002 + arm) * 0.2;
      graphics.lineStyle(2, colorNum, alpha);
      graphics.beginPath();

      // Jagged spiral with few joints
      const joints = 10;
      let firstPoint = true;

      for (let i = 0; i < joints; i++) {
        const t = i / joints;
        const angle = rotation + armOffset + t * Math.PI * 2.5;
        const dist = t * radius * 0.8;

        // Add wobble/jitter
        const wobbleX = Math.sin(wobbleTime * 0.003 + i * 1.5 + arm) * 5;
        const wobbleY = Math.cos(wobbleTime * 0.0025 + i * 1.3 + arm) * 5;
        const jitterX = ((i * 17 + arm * 31) % 9 - 4) * 2;
        const jitterY = ((i * 23 + arm * 41) % 9 - 4) * 2;

        const px = Math.cos(angle) * dist + wobbleX + jitterX;
        const py = Math.sin(angle) * dist + wobbleY + jitterY;

        if (firstPoint) {
          graphics.moveTo(px, py);
          firstPoint = false;
        } else {
          graphics.lineTo(px, py);
        }
      }
      graphics.strokePath();
    }

    // Inner glow
    graphics.lineStyle(4, colorNum, 0.12);
    graphics.beginPath();
    let first = true;
    for (let i = 0; i < 8; i++) {
      const t = i / 8;
      const angle = rotation + t * Math.PI * 2;
      const dist = t * radius * 0.5;
      const wobble = Math.sin(wobbleTime * 0.002 + i) * 3;
      const px = Math.cos(angle) * dist + wobble;
      const py = Math.sin(angle) * dist + wobble;
      if (first) {
        graphics.moveTo(px, py);
        first = false;
      } else {
        graphics.lineTo(px, py);
      }
    }
    graphics.strokePath();
  }

  private flashRiftLightning(graphics: Phaser.GameObjects.Graphics, radius: number, colorNum: number): void {
    graphics.clear();

    // Single jagged bolt made of right angles
    const alpha = 0.95;
    graphics.lineStyle(2, colorNum, alpha);

    const maxX = Math.floor(radius * 0.6);
    const maxY = Math.floor(radius * 0.6);

    const startX = Phaser.Math.Between(-maxX, maxX);
    const startY = Phaser.Math.Between(-maxY, maxY);
    graphics.beginPath();
    graphics.moveTo(startX, startY);

    const segments = Phaser.Math.Between(5, 8);
    let cx = startX;
    let cy = startY;

    for (let i = 0; i < segments; i++) {
      const isHorizontal = i % 2 === 0;
      const delta = Phaser.Math.Between(Math.floor(radius * 0.08), Math.floor(radius * 0.18));
      const sign = Phaser.Math.Between(0, 1) === 0 ? -1 : 1;

      if (isHorizontal) {
        cx = Phaser.Math.Clamp(cx + sign * delta, -maxX, maxX);
      } else {
        cy = Phaser.Math.Clamp(cy + sign * delta, -maxY, maxY);
      }

      graphics.lineTo(cx, cy);
    }

    graphics.strokePath();

    graphics.alpha = 1;
    this.tweens.add({
      targets: graphics,
      alpha: { from: 1, to: 0 },
      duration: 180,
      ease: 'Linear',
      onComplete: () => {
        graphics.clear();
      },
    });
  }

  /**
   * Draw dense cloud particles for world map rift storm
   */
  private drawWorldMapRiftClouds(graphics: Phaser.GameObjects.Graphics, x: number, y: number, radius: number, colorNum: number, time: number): void {
    graphics.clear();

    // Draw cloud blobs that drift around the storm
    const numClouds = 24;
    const seed = x * 1000 + y;

    for (let i = 0; i < numClouds; i++) {
      const angle = (i / numClouds) * Math.PI * 2 + time * 0.0004;
      const dist = (0.35 + ((seed + i * 137) % 100) / 180) * radius;
      const cloudX = x + Math.cos(angle) * dist;
      const cloudY = y + Math.sin(angle) * dist;
      const cloudSize = 6 + ((seed + i * 53) % 8);
      const alpha = 0.12 + ((seed + i * 31) % 10) / 60;

      graphics.fillStyle(colorNum, alpha);
      graphics.fillRect(cloudX - cloudSize / 2, cloudY - cloudSize / 2, cloudSize, cloudSize);
    }

    // Inner dense clouds
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2 - time * 0.0006;
      const dist = (0.15 + ((seed + i * 89) % 100) / 400) * radius;
      const cloudX = x + Math.cos(angle) * dist;
      const cloudY = y + Math.sin(angle) * dist;
      const cloudSize = 8 + ((seed + i * 41) % 10);
      const alpha = 0.18 + ((seed + i * 17) % 10) / 50;

      graphics.fillStyle(colorNum, alpha);
      graphics.fillRect(cloudX - cloudSize / 2, cloudY - cloudSize / 2, cloudSize, cloudSize);
    }
  }

  /**
   * Add a rift storm marker to the world map
   */
  addRiftStormMarker(data: { id: string; x: number; y: number; radius: number; tier: string; color: string; expiresAt: number }): void {
    if (!this.canCreateGameObjects()) return;
    
    // Remove existing marker if present
    this.removeRiftStormMarker(data.id);

    // Use purple consistently for all rift storms
    const purpleColor = 0x9b59b6;
    const colorNum = purpleColor;

    const graphics = this.add.graphics();
    graphics.setDepth(50);

    // Draw concentric squares (grid aesthetic) with purple
    const numRings = 4;
    for (let i = 0; i < numRings; i++) {
      const ringRadius = data.radius * (1 - i * 0.2);
      const alpha = 0.15 + i * 0.08;
      
      // Fill
      graphics.fillStyle(colorNum, alpha);
      graphics.fillRect(data.x - ringRadius, data.y - ringRadius, ringRadius * 2, ringRadius * 2);
      
      // Border
      graphics.lineStyle(2, colorNum, 0.5 + i * 0.1);
      graphics.strokeRect(data.x - ringRadius, data.y - ringRadius, ringRadius * 2, ringRadius * 2);
    }

    // Add pulsing animation
    this.tweens.add({
      targets: graphics,
      alpha: { from: 1, to: 0.6 },
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Dense clouds layer
    const clouds = this.add.graphics();
    clouds.setDepth(51);
    let cloudTime = 0;
    const cloudTween = this.tweens.addCounter({
      from: 0,
      to: 100000,
      duration: 100000,
      repeat: -1,
      onUpdate: (tween) => {
        cloudTime = tween.getValue() ?? 0;
        this.drawWorldMapRiftClouds(clouds, data.x, data.y, data.radius, colorNum, cloudTime);
      },
    });

    // Rotating spiral overlay (centered) with wobble
    const spiral = this.add.graphics();
    spiral.setDepth(52);
    spiral.setPosition(data.x, data.y);
    let spiralRotation = 0;
    let spiralWobbleTime = 0;
    const spiralTween = this.tweens.addCounter({
      from: 0,
      to: 100000,
      duration: 100000,
      repeat: -1,
      onUpdate: (tween) => {
        spiralWobbleTime = tween.getValue() ?? 0;
        spiralRotation = (spiralWobbleTime / 100000) * Math.PI * 2 * 8;
        this.createRiftSpiral(spiral, data.radius, colorNum, spiralRotation, spiralWobbleTime);
      },
    });

    // Lightning overlay (centered)
    const lightning = this.add.graphics();
    lightning.setDepth(53);
    lightning.setPosition(data.x, data.y);
    lightning.alpha = 0;

    // Use a one-shot timer that re-schedules itself
    const scheduleLightning = (): Phaser.Time.TimerEvent => {
      return this.time.addEvent({
        delay: Phaser.Math.Between(700, 1500),
        loop: false,
        callback: () => {
          this.flashRiftLightning(lightning, data.radius, colorNum);
          const marker = this.riftStormMarkers.get(data.id);
          if (marker) {
            marker.lightningTimer = scheduleLightning();
          }
        },
      });
    };

    const lightningTimer = scheduleLightning();

    // Label with purple color
    const tierLabel = data.tier.toUpperCase();
    const initialRemainingSeconds = Math.max(0, Math.ceil((data.expiresAt - Date.now()) / 1000));
    const initialTimerText = this.formatCountdownSeconds(initialRemainingSeconds);
    const label = this.createSharpText(data.x, data.y - data.radius - 15, `[RIFT - ${tierLabel}] ${initialTimerText}`,
    {
      fontSize: '9px',
      color: '#9b59b6',
      backgroundColor: '#000000cc',
      padding: { x: 4, y: 2 },
    });
    label.setOrigin(0.5);
    label.setDepth(54);

    const countdownTimer = this.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        this.updateRiftStormLabel(data.id);
      },
    });

    this.riftStormMarkers.set(data.id, {
      graphics,
      spiral,
      lightning,
      clouds,
      label,
      spiralTween,
      lightningTimer,
      countdownTimer,
      cloudTween,
      expiresAt: data.expiresAt,
      tier: data.tier,
    });

    this.updateRiftStormLabel(data.id);
  }

  /**
   * Remove a rift storm marker from the world map
   */
  removeRiftStormMarker(id: string): void {
    const marker = this.riftStormMarkers.get(id);
    if (marker) {
      marker.graphics.destroy();
      marker.spiralTween.destroy();
      marker.lightningTimer.destroy();
      marker.countdownTimer.destroy();
      marker.cloudTween.destroy();
      marker.spiral.destroy();
      marker.lightning.destroy();
      marker.clouds.destroy();
      marker.label.destroy();
      this.riftStormMarkers.delete(id);
    }
  }

  /**
   * Update all rift storm markers (called from React)
   */
  updateRiftStorms(storms: Array<{ id: string; x: number; y: number; radius: number; tier: string; color: string; expiresAt: number }>): void {
    // Remove markers that no longer exist
    const currentIds = new Set(storms.map(s => s.id));
    const markersToRemove: string[] = [];
    this.riftStormMarkers.forEach((_, id) => {
      if (!currentIds.has(id)) {
        markersToRemove.push(id);
      }
    });
    markersToRemove.forEach(id => this.removeRiftStormMarker(id));

    // Add/update markers
    for (const storm of storms) {
      if (!this.riftStormMarkers.has(storm.id)) {
        this.addRiftStormMarker(storm);
      } else {
        const marker = this.riftStormMarkers.get(storm.id);
        if (marker) {
          marker.expiresAt = storm.expiresAt;
          marker.tier = storm.tier;
          this.updateRiftStormLabel(storm.id);
        }
      }
    }
  }

  // ============ PIRATE RAID MARKERS ============

  private pirateRaidMarkers: Map<string, {
    graphics: Phaser.GameObjects.Graphics;
    label: Phaser.GameObjects.Text;
    pulseAnimation: Phaser.Tweens.Tween;
  }> = new Map();

  private addPirateRaidMarker(islandX: number, islandY: number, islandName: string, raidId: string): void {
    if (!this.canCreateGameObjects()) return;

    const graphics = this.add.graphics();
    graphics.setDepth(45);

    // Draw pulsing red X pattern for raid
    const size = 40;
    const lineWidth = 2;
    graphics.lineStyle(lineWidth, 0xff3333, 0.8);
    
    // Draw X
    graphics.moveTo(islandX - size, islandY - size);
    graphics.lineTo(islandX + size, islandY + size);
    graphics.moveTo(islandX + size, islandY - size);
    graphics.lineTo(islandX - size, islandY + size);
    graphics.strokePath();

    // Draw outer square
    graphics.lineStyle(1, 0xaa0000, 0.6);
    graphics.strokeRect(islandX - size, islandY - size, size * 2, size * 2);

    // Pulse animation
    const pulseAnimation = this.tweens.add({
      targets: graphics,
      alpha: { from: 1, to: 0.4 },
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Label
    const label = this.createSharpText(islandX, islandY + size + 15, '[RAID]', {
      fontSize: '9px',
      color: '#ff3333',
      backgroundColor: '#000000cc',
      padding: { x: 4, y: 2 },
    });
    label.setOrigin(0.5);
    label.setDepth(46);

    this.pirateRaidMarkers.set(raidId, {
      graphics,
      label,
      pulseAnimation,
    });
  }

  private removePirateRaidMarker(raidId: string): void {
    const marker = this.pirateRaidMarkers.get(raidId);
    if (marker) {
      marker.graphics.destroy();
      marker.label.destroy();
      marker.pulseAnimation.destroy();
      this.pirateRaidMarkers.delete(raidId);
    }
  }

  // ============ SEA MONSTER MARKERS ============

  private seaMonsterMarkers: Map<string, {
    graphics: Phaser.GameObjects.Graphics;
    label: Phaser.GameObjects.Text;
    rotationAnimation: Phaser.Tweens.Tween;
  }> = new Map();

  private addSeaMonsterMarker(x: number, y: number, monsterId: string): void {
    if (!this.canCreateGameObjects()) return;

    const graphics = this.add.graphics();
    graphics.setDepth(40);
    graphics.setPosition(x, y);

    // Draw rotating diamond pattern for sea monster
    const size = 30;
    let rotation = 0;

    const rotationAnimation = this.tweens.addCounter({
      from: 0,
      to: Math.PI * 2,
      duration: 3000,
      repeat: -1,
      onUpdate: (tween) => {
        rotation = tween.getValue() ?? 0;
        graphics.clear();

        // Draw rotating diamond
        graphics.lineStyle(2, 0x1a3a5a, 0.9);
        graphics.beginPath();
        for (let i = 0; i < 4; i++) {
          const angle = rotation + (i / 4) * Math.PI * 2;
          const px = Math.cos(angle) * size;
          const py = Math.sin(angle) * size;
          if (i === 0) {
            graphics.moveTo(px, py);
          } else {
            graphics.lineTo(px, py);
          }
        }
        graphics.closePath();
        graphics.strokePath();

        // Inner circle
        graphics.lineStyle(1, 0x1a3a5a, 0.5);
        graphics.strokeCircle(0, 0, size * 0.5);
      },
    });

    // Label
    const label = this.createSharpText(x, y + size + 15, '[MONSTER]', {
      fontSize: '9px',
      color: '#1a3a5a',
      backgroundColor: '#000000cc',
      padding: { x: 4, y: 2 },
    });
    label.setOrigin(0.5);
    label.setDepth(41);

    this.seaMonsterMarkers.set(monsterId, {
      graphics,
      label,
      rotationAnimation,
    });
  }

  private removeSeaMonsterMarker(monsterId: string): void {
    const marker = this.seaMonsterMarkers.get(monsterId);
    if (marker) {
      marker.graphics.destroy();
      marker.label.destroy();
      marker.rotationAnimation.destroy();
      this.seaMonsterMarkers.delete(monsterId);
    }
  }

  updateSeaMonsters(monsters: Array<{ id: string; x: number; y: number; type: string }>): void {
    const currentIds = new Set(monsters.map(m => m.id));
    const markersToRemove: string[] = [];
    this.seaMonsterMarkers.forEach((_, id) => {
      if (!currentIds.has(id)) {
        markersToRemove.push(id);
      }
    });
    markersToRemove.forEach(id => this.removeSeaMonsterMarker(id));

    // Add new markers
    for (const monster of monsters) {
      if (!this.seaMonsterMarkers.has(monster.id)) {
        this.addSeaMonsterMarker(monster.x, monster.y, monster.id);
      }
    }
  }

  updateRiftScars(scars: Array<{ id: string; x: number; y: number; name: string }>): void {
    const currentIds = new Set(scars.map(s => s.id));
    const markersToRemove: string[] = [];
    this.riftScarMarkers.forEach((_, id) => {
      if (!currentIds.has(id)) {
        markersToRemove.push(id);
      }
    });
    markersToRemove.forEach(id => this.removeRiftScarMarker(id));

    // Add new markers
    for (const scar of scars) {
      if (!this.riftScarMarkers.has(scar.id)) {
        this.addRiftScarMarker(scar.x, scar.y, scar.id, scar.name);
      }
    }
  }

  private addRiftScarMarker(x: number, y: number, scarId: string, name: string): void {
    if (!this.canCreateGameObjects()) return;

    const container = this.add.container(x, y);
    container.setDepth(150); // Above islands but below UI

    // Create glowing portal effect
    const glow = this.add.graphics();
    const portal = this.add.graphics();

    // Draw outer glow (pulsing)
    glow.fillStyle(0x9d4edd, 0.3);
    glow.fillCircle(0, 0, 20);
    glow.fillStyle(0x9d4edd, 0.5);
    glow.fillCircle(0, 0, 15);

    // Draw portal (swirling vortex)
    portal.lineStyle(3, 0xc77dff, 1);
    portal.strokeCircle(0, 0, 12);
    portal.lineStyle(2, 0xe0aaff, 0.8);
    portal.strokeCircle(0, 0, 8);
    portal.lineStyle(1, 0xffffff, 0.6);
    portal.strokeCircle(0, 0, 4);

    // Add center dot
    portal.fillStyle(0xffffff, 0.9);
    portal.fillCircle(0, 0, 2);

    container.add([glow, portal]);

    // Make interactive
    const hitArea = new Phaser.Geom.Circle(0, 0, 20);
    container.setInteractive(hitArea, Phaser.Geom.Circle.Contains);
    container.on('pointerover', () => {
      container.setScale(1.2);
      // Show tooltip
      const tooltip = this.add.text(x, y - 30, `Rift Scar: ${name}`, {
        fontSize: '12px',
        color: '#ffffff',
        backgroundColor: '#000000',
        padding: { x: 6, y: 4 },
      });
      tooltip.setOrigin(0.5, 1);
      tooltip.setDepth(200);
      container.setData('tooltip', tooltip);
    });
    container.on('pointerout', () => {
      container.setScale(1);
      const tooltip = container.getData('tooltip');
      if (tooltip) tooltip.destroy();
    });
    container.on('pointerdown', () => {
      console.log(`[WORLD MAP] Clicked rift scar: ${name} (${scarId})`);
      // TODO: Show scar details panel
    });

    // Pulsing animation
    const pulseTimer = this.time.addEvent({
      delay: 2000,
      loop: true,
      callback: () => {
        if (!this.canCreateGameObjects()) return;
        this.tweens.add({
          targets: glow,
          alpha: 0.1,
          duration: 1000,
          yoyo: true,
          ease: 'Sine.easeInOut',
        });
      },
    });

    this.riftScarMarkers.set(scarId, {
      container,
      portal,
      glow,
      pulseTimer,
      data: { id: scarId, x, y, name },
    });
  }

  private removeRiftScarMarker(scarId: string): void {
    const marker = this.riftScarMarkers.get(scarId);
    if (!marker) return;

    marker.pulseTimer.destroy();
    const tooltip = marker.container.getData('tooltip');
    if (tooltip) tooltip.destroy();
    marker.container.destroy();
    this.riftScarMarkers.delete(scarId);
  }

  // ============ WHIRLPOOL INSTANCE MODE ============

  enterWhirlpoolMode(instance: WhirlpoolInstanceMapData): void {
    this.isInWhirlpool = true;
    this.whirlpoolInstance = instance;

    // Hide overworld elements
    this.oceanGraphics.setVisible(false);
    this.gridGraphics.setVisible(false);
    this.islands.forEach(sprite => sprite.setVisible(false));
    if (this.destinationMarker) this.destinationMarker.setVisible(false);

    // Create instance container
    this.instanceContainer = this.add.container(0, 0);
    this.instanceContainer.setDepth(200);

    // Draw instance background
    const bg = this.add.graphics();
    bg.fillStyle(0x0a0a1a, 1);
    bg.fillRect(0, 0, instance.width, instance.height);
    this.instanceContainer.add(bg);

    // Draw grid
    const grid = this.add.graphics();
    grid.lineStyle(1, 0x1a1a2e, 0.3);
    for (let x = 0; x <= instance.width; x += TILE_SIZE) {
      grid.moveTo(x, 0);
      grid.lineTo(x, instance.height);
    }
    for (let y = 0; y <= instance.height; y += TILE_SIZE) {
      grid.moveTo(0, y);
      grid.lineTo(instance.width, y);
    }
    grid.strokePath();
    this.instanceContainer.add(grid);

    // Draw hazards
    const hazardColors: Record<string, number> = {
      // Original hazards
      rogue_wave: 0x4169e1,
      whirlpool_pull: 0x00ced1,
      fog_bank: 0x708090,
      cursed_waters: 0x8b008b,
      sea_mist: 0xa9a9a9,
      // New RuneScape-inspired hazards
      tidal_surge: 0x1e90ff,
      lightning_storm: 0xffff00,
      void_rift: 0x9932cc,
      blood_pool: 0xdc143c,
      ice_barrier: 0x00ffff,
      flame_geyser: 0xff4500,
      poison_cloud: 0x32cd32,
      gravity_well: 0x4169e1,
      shadow_vortex: 0x2f4f4f,
      crystal_spikes: 0xb0e0e6,
    };
    for (const hazard of instance.hazards) {
      if (!hazard.active) continue;
      const hg = this.add.graphics();
      const color = hazardColors[hazard.type] || 0xff0000;
      hg.fillStyle(color, 0.2);
      hg.fillRect(
        hazard.tileX * TILE_SIZE - hazard.radius * TILE_SIZE,
        hazard.tileY * TILE_SIZE - hazard.radius * TILE_SIZE,
        hazard.radius * 2 * TILE_SIZE,
        hazard.radius * 2 * TILE_SIZE
      );
      hg.lineStyle(1, color, 0.5);
      hg.strokeRect(
        hazard.tileX * TILE_SIZE - hazard.radius * TILE_SIZE,
        hazard.tileY * TILE_SIZE - hazard.radius * TILE_SIZE,
        hazard.radius * 2 * TILE_SIZE,
        hazard.radius * 2 * TILE_SIZE
      );
      this.instanceContainer.add(hg);
    }

    // Draw islands
    const biomeColors: Record<string, number> = {
      flotsam: 0x8b7355,
      coral: 0xff6b9d,
      storm: 0x4a5568,
      sunken: 0x2d5a4a,
      cursed: 0x4a1a4a,
    };
    for (const island of instance.islands) {
      const ig = this.add.graphics();
      const color = biomeColors[island.biome] || 0x8b7355;
      ig.fillStyle(color, 0.8);
      ig.fillRect(
        island.x - island.radius,
        island.y - island.radius,
        island.radius * 2,
        island.radius * 2
      );
      ig.lineStyle(2, 0xffffff, 0.3);
      ig.strokeRect(
        island.x - island.radius,
        island.y - island.radius,
        island.radius * 2,
        island.radius * 2
      );
      this.instanceContainer.add(ig);
    }

    // Draw exit point
    const exitG = this.add.graphics();
    const exitColor = instance.exitPoint.stable ? 0x00ff88 : 0xffaa00;
    exitG.fillStyle(exitColor, 0.6);
    exitG.fillRect(
      instance.exitPoint.worldX - TILE_SIZE,
      instance.exitPoint.worldY - TILE_SIZE,
      TILE_SIZE * 2,
      TILE_SIZE * 2
    );
    exitG.lineStyle(2, exitColor, 1);
    exitG.strokeRect(
      instance.exitPoint.worldX - TILE_SIZE,
      instance.exitPoint.worldY - TILE_SIZE,
      TILE_SIZE * 2,
      TILE_SIZE * 2
    );
    this.instanceContainer.add(exitG);

    // Exit label
    const exitLabel = this.createSharpText(
      instance.exitPoint.worldX,
      instance.exitPoint.worldY - TILE_SIZE * 2,
      instance.exitPoint.stable ? 'EXIT' : 'EXIT [UNSTABLE]',
      { fontSize: '10px', color: instance.exitPoint.stable ? '#00ff88' : '#ffaa00' }
    );
    exitLabel.setOrigin(0.5, 1);
    this.instanceContainer.add(exitLabel);

    // Instance header
    const classColors: Record<string, string> = {
      C1: '#4fc3f7', C2: '#29b6f6', C3: '#7e57c2',
      C4: '#ab47bc', C5: '#ef5350', C6: '#d32f2f',
    };
    const header = this.createSharpText(
      instance.width / 2,
      20,
      `${instance.className} [${instance.class}]`,
      { fontSize: '14px', color: classColors[instance.class] || '#ffffff' }
    );
    header.setOrigin(0.5, 0);
    this.instanceContainer.add(header);

    // Effect label
    const effectLabel = this.createSharpText(
      instance.width / 2,
      38,
      `Effect: ${instance.effect.replace(/_/g, ' ').toUpperCase()}`,
      { fontSize: '10px', color: '#aaaaaa' }
    );
    effectLabel.setOrigin(0.5, 0);
    this.instanceContainer.add(effectLabel);

    // Adjust camera bounds
    this.camera.setBounds(0, 0, instance.width, instance.height);
    this.camera.centerOn(instance.width / 2, instance.height / 2);
  }

  exitWhirlpoolMode(): void {
    if (!this.isInWhirlpool) return;

    this.isInWhirlpool = false;
    this.whirlpoolInstance = null;

    // Destroy instance container
    if (this.instanceContainer) {
      this.instanceContainer.destroy();
      this.instanceContainer = null;
    }

    // Show overworld elements
    this.oceanGraphics.setVisible(true);
    this.gridGraphics.setVisible(true);
    this.islands.forEach(sprite => sprite.setVisible(true));
    if (this.destinationMarker) this.destinationMarker.setVisible(true);

    // Reset camera bounds
    this.camera.setBounds(
      -UNCHARTED_WATERS_BUFFER_PX,
      -UNCHARTED_WATERS_BUFFER_PX,
      this.worldWidth + UNCHARTED_WATERS_BUFFER_PX * 2,
      this.worldHeight + UNCHARTED_WATERS_BUFFER_PX * 2
    );
    this.camera.centerOn(this.playerPosition.x, this.playerPosition.y);
  }

  isInWhirlpoolMode(): boolean {
    return this.isInWhirlpool;
  }

  /**
   * Point-in-polygon test using ray casting algorithm
   */
  private pointInPolygon(x: number, y: number, polygon: Point[]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;
      
      const intersect = ((yi > y) !== (yj > y)) &&
        (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }

  update(): void {
    // Load chunks as camera moves
    this.loadVisibleChunks();
  }

  private applyMomentum(velocityX: number, velocityY: number): void {
    // Only apply momentum if velocity is significant
    const speed = Math.sqrt(velocityX * velocityX + velocityY * velocityY);
    if (speed < 2) return;
    
    // Cancel any existing momentum
    if (this.momentumTween) {
      this.momentumTween.stop();
    }
    
    // Apply momentum with deceleration
    const momentumObject = { vx: velocityX, vy: velocityY };
    
    this.momentumTween = this.tweens.add({
      targets: momentumObject,
      vx: 0,
      vy: 0,
      duration: 500,
      ease: 'Power2',
      onUpdate: () => {
        // Apply velocity to camera scroll (inverted because drag moves camera opposite to pointer)
        this.camera.scrollX -= momentumObject.vx / this.camera.zoom;
        this.camera.scrollY -= momentumObject.vy / this.camera.zoom;
      },
      onComplete: () => {
        this.momentumTween = null;
      }
    });
  }
}
